import { useEffect } from 'react';

type ScriptSlot = 'head' | 'body-start' | 'body-end';

interface CustomScriptsInjectorProps {
  headCode?: string | null;
  bodyStartCode?: string | null;
  bodyEndCode?: string | null;
}

const SLOT_ATTR = 'data-lumenise-custom-script-slot';

function createExecutableScript(slot: ScriptSlot, code: string, attributes: NamedNodeMap | null = null): HTMLScriptElement {
  const script = document.createElement('script');
  if (attributes) {
    for (const attr of Array.from(attributes)) {
      script.setAttribute(attr.name, attr.value);
    }
  }
  script.text = code;
  script.setAttribute(SLOT_ATTR, slot);
  script.setAttribute('data-lumenise-custom-script-source', 'plain-text');
  return script;
}

function cloneExecutableNode(node: Node, slot: ScriptSlot): Node {
  if (node.nodeType === Node.TEXT_NODE) {
    const code = node.textContent?.trim();
    if (!code) return document.createTextNode('');

    return createExecutableScript(slot, code);
  }

  if (!(node instanceof HTMLScriptElement)) {
    const clone = node.cloneNode(true);
    if (clone instanceof HTMLElement) clone.setAttribute(SLOT_ATTR, slot);
    return clone;
  }

  const script = document.createElement('script');
  for (const attr of Array.from(node.attributes)) {
    script.setAttribute(attr.name, attr.value);
  }
  script.text = node.text;
  script.setAttribute(SLOT_ATTR, slot);
  return script;
}

function unwrapSingleScript(trimmed: string): { attributes: NamedNodeMap; code: string } | null {
  const template = document.createElement('template');
  const openingTag = trimmed.match(/^<script\b[^>]*>/i)?.[0];
  const closingTagIndex = trimmed.toLowerCase().lastIndexOf('</script>');

  if (!openingTag || closingTagIndex === -1) return null;
  if (trimmed.slice(closingTagIndex + '</script>'.length).trim()) return null;

  template.innerHTML = openingTag;
  const script = template.content.firstElementChild;
  if (!(script instanceof HTMLScriptElement)) return null;

  return {
    attributes: script.attributes,
    code: trimmed.slice(openingTag.length, closingTagIndex).trim(),
  };
}

function mountCode(slot: ScriptSlot, code: string | null | undefined): Node[] {
  const trimmed = code?.trim();
  if (!trimmed) return [];

  if (!trimmed.startsWith('<')) {
    return insertNodes(slot, [createExecutableScript(slot, trimmed)]);
  }

  const singleScript = unwrapSingleScript(trimmed);
  if (singleScript) {
    return insertNodes(slot, [createExecutableScript(slot, singleScript.code, singleScript.attributes)]);
  }

  const template = document.createElement('template');
  template.innerHTML = trimmed;

  const originalNodes = Array.from(template.content.childNodes);
  const hasHtmlNodes = originalNodes.some((node) => node.nodeType === Node.ELEMENT_NODE);
  const nodes = hasHtmlNodes
    ? originalNodes
        .filter((node) => node.nodeType !== Node.TEXT_NODE)
        .map((node) => cloneExecutableNode(node, slot))
    : [cloneExecutableNode(document.createTextNode(trimmed), slot)];
  return insertNodes(slot, nodes);
}

function insertNodes(slot: ScriptSlot, nodes: Node[]): Node[] {
  const target = slot === 'head' ? document.head : document.body;

  if (slot === 'body-start') {
    const anchor = document.body.firstChild;
    nodes.forEach((node) => {
      try {
        target.insertBefore(node, anchor);
      } catch (error) {
        console.error('Erro ao injetar script personalizado.', error);
      }
    });
    return nodes;
  }

  nodes.forEach((node) => {
    try {
      target.appendChild(node);
    } catch (error) {
      console.error('Erro ao injetar script personalizado.', error);
    }
  });
  return nodes;
}

export function CustomScriptsInjector({ headCode, bodyStartCode, bodyEndCode }: CustomScriptsInjectorProps) {
  useEffect(() => {
    const mounted = [
      ...mountCode('head', headCode),
      ...mountCode('body-start', bodyStartCode),
      ...mountCode('body-end', bodyEndCode),
    ];

    return () => {
      mounted.forEach((node) => node.parentNode?.removeChild(node));
    };
  }, [headCode, bodyStartCode, bodyEndCode]);

  return null;
}
